import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import GoogleMapReact, { Coords } from 'google-map-react';

// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedForm from '../../../components/form';
import { ActionCustom, ActionFunction, FormDatas } from '../../../local-types';
import MainLoading from '../../../components/main-loading';

// Modules
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import {
    actions as towersActions,
    selectors as towersSelectors,
    types as towersTypes,
} from '../../../redux/reducers/towers';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { Tower } from '../../../redux/reducers/api-types';

// Resources
import withGlobalStyles from '../../../utils/global-styles';
import MapUtils from '../../../utils/mapUtils';
import { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { Icon } from '@material-ui/core';
import Dialog, { DialogTextColor } from '../../../components/dialog/dialog';
import { CIRCLE_COLOR_DEFAULT } from '../../../utils/constants';
import envConfig from '../../../config';

const queryString = require('query-string');

interface ControlTowersAddProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    center: Coords;
    zoom: number;
    towers: Tower[];
    cudTowersError: ErrorPayload;
    isSavingTower: boolean;
    getTowers: () => void;
    createTower: (payload: Omit<Tower, 'id'>) => void;
    editTower: (id: string, payload: Omit<Tower, 'id'>) => void;
    deleteTower: (id: string) => void;
}

interface ControlTowersAddState {
    formData: Partial<Tower>;
}

class ControlTowersAdd extends React.Component<ControlTowersAddProps, ControlTowersAddState> {
    static defaultProps = {
        center: MapUtils.defaultCenter,
        zoom: MapUtils.defaultZoom,
    };

    map: any;
    maps: any;
    marker: any;
    circle: any;
    updateTimeout: NodeJS.Timeout | null = null;

    myFormRef: any;

    state = {
        formData: {
            name: '',
            radius: 5000,
            lat: this.props.center.lat,
            lng: this.props.center.lng,
            position: {
                lat: this.props.center.lat,
                lng: this.props.center.lng,
            },
        },
    };

    componentDidMount() {
        const { getTowers, towers } = this.props;
        if (!towers.length) {
            getTowers();
        }
    }

    componentDidUpdate(prevProps: Readonly<ControlTowersAddProps>, prevState: Readonly<ControlTowersAddState>): void {
        const { towers, location } = this.props;
        const { id } = queryString.parse(location.search);

        if (id && prevProps.towers !== towers) {
            const tower = towers?.find((t) => t.id === id);
            if (tower) {
                this.setState({ formData: { ...tower } });
            }
        }
    }

    handleApiLoaded = (map: any, maps: any) => {
        this.map = map;
        this.maps = maps;

        const { formData } = this.state;
        if (formData.position.lat && formData.position.lng && formData.radius) {
            this.map.setCenter({ lat: formData.position.lat, lng: formData.position.lng });
            const bounds = new this.maps.LatLngBounds();
            const radiusKm = formData.radius / 1000;
            bounds.extend({ lat: formData.position.lat + radiusKm / 111, lng: formData.position.lng });
            bounds.extend({ lat: formData.position.lat - radiusKm / 111, lng: formData.position.lng });
            bounds.extend({
                lat: formData.position.lat,
                lng: formData.position.lng + radiusKm / (111 * Math.cos(formData.position.lat * (Math.PI / 180))),
            });
            bounds.extend({
                lat: formData.position.lat,
                lng: formData.position.lng - radiusKm / (111 * Math.cos(formData.position.lat * (Math.PI / 180))),
            });
            this.map.fitBounds(bounds);
        }

        this.updateMarker();
    };

    updateMarker(formData = this.state.formData) {
        if (this.marker) {
            this.marker.setMap(null);
        }
        if (this.circle) {
            this.circle.setMap(null);
        }

        if (this.map && this.maps && formData.position) {
            this.marker = new this.maps.Marker({
                position: formData.position,
                map: this.map,
                draggable: true,
            });

            // Create a circle for the tower's radius
            this.circle = new this.maps.Circle({
                strokeColor: CIRCLE_COLOR_DEFAULT,
                strokeOpacity: 0.8,
                strokeWeight: 2,
                fillColor: CIRCLE_COLOR_DEFAULT,
                fillOpacity: 0.15,
                map: this.map,
                center: formData.position,
                radius: formData.radius || 5, // in meters
            });

            this.maps.event.addListener(this.marker, 'dragend', () => {
                const lat = Number(this.marker.getPosition().lat().toFixed(6));
                const lng = Number(this.marker.getPosition().lng().toFixed(6));
                const position = { lat, lng };
                this.setState({
                    formData: {
                        ...this.state.formData,
                        position,
                    },
                });
                this.myFormRef.setValue('position', position);
                this.circle.setCenter(position);
                this.circle.setRadius(formData.radius || 5);
            });

            this.maps.event.addListener(this.map, 'click', (e) => {
                const lat = Number(e.latLng.lat().toFixed(6));
                const lng = Number(e.latLng.lng().toFixed(6));
                const position = { lat, lng };
                this.setState({
                    formData: {
                        ...this.state.formData,
                        position,
                    },
                });
                this.myFormRef.setValue('position', position);
                this.circle.setCenter(position);
                this.circle.setRadius(this.state.formData.radius || 5);
                this.marker.setPosition(position);
            });
        }
    }

    handleFormChange = (formData: any) => {
        if (this.updateTimeout) {
            clearTimeout(this.updateTimeout);
        }

        this.updateTimeout = setTimeout(() => {
            if (this.map) {
                this.updateMarker(formData);
            }
        }, 300);
    };

    handleSubmit = (formData: any) => {
        const { location, createTower, editTower } = this.props;
        const { id } = queryString.parse(location.search);

        const payload = {
            name: formData.name,
            radius: parseFloat(formData.radius),
            ...formData.position,
        };

        if (id) {
            editTower(id, payload);
        } else {
            createTower(payload);
        }
    };

    generateInputs = (): FormDatas[] => {
        const { t, location } = this.props;
        const { id } = queryString.parse(location.search);

        return [
            {
                title: t('common:form.general_infos'),
                sectionStyle: { flex: 1 },
                datas: [
                    {
                        id: 'name',
                        label: t('name'),
                        required: true,
                    },
                ],
            },
            {
                title: t('common:form.address_infos'),
                sectionStyle: { flex: 2 },
                datas: [
                    {
                        id: 'position[lat]',
                        label: t('latitude'),
                        type: 'number',
                        numberNegative: true,
                        required: true,
                        inputsAttributs: { step: 0.000001 },
                    },
                    {
                        id: 'position[lng]',
                        label: t('longitude'),
                        type: 'number',
                        numberNegative: true,
                        required: true,
                        inputsAttributs: { step: 0.000001 },
                    },
                    {
                        id: 'radius',
                        label: t('common:coord.radius'),
                        type: 'number',
                        required: true,
                        inputsAttributs: { step: 1 },
                    },
                    {
                        id: 'map',
                        label: t('map'),
                        required: true,
                        customView: (val, handleChange) => (
                            <div style={{ height: '400px', width: '100%' }}>
                                <GoogleMapReact
                                    bootstrapURLKeys={{ key: envConfig.google_key }}
                                    defaultCenter={this.props.center}
                                    defaultZoom={this.props.zoom}
                                    yesIWantToUseGoogleMapApiInternals
                                    onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}
                                />
                            </div>
                        ),
                    },
                ],
            },
        ];
    };

    getActions = (id, name: string): ActionCustom[] => {
        const { t } = this.props;
        const actions: ActionCustom[] = [];
        if (id) {
            actions.push({
                label: t('common:delete'),
                customView: (
                    <Dialog
                        btnTitle={`${t('common:delete')} ${name}`}
                        description={t('common:deleteParam', { id })}
                        customButton={
                            <div style={{ display: 'flex', gap: 8 }}>
                                <Icon>clear</Icon>
                                {t('common:delete')}
                            </div>
                        }
                        btnKOText={t('common:cancel')}
                        btnOKColor={DialogTextColor.RED}
                        btnOKText={t('common:delete')}
                        onOK={() => this.props.deleteTower(id)}
                    />
                ),
            });
        }
        return actions;
    };

    render() {
        const { t, cudTowersError, isSavingTower } = this.props;
        const { formData } = this.state;
        const { id } = queryString.parse(this.props.location.search);

        const isLoading = id && !formData.name;

        return (
            <App
                needRights={{ name: AccountRight.DRIVER_PREBOOK, mode: RightRank.W }}
                header={{
                    title: t(id ? 'edit' : 'add'),
                    actions: this.getActions(id, formData?.name),
                }}
            >
                <ErrorMessage payload={cudTowersError} />
                {isLoading && <MainLoading />}
                {!isLoading && formData && (
                    <EnhancedForm
                        onRef={(ref) => (this.myFormRef = ref)}
                        payload={formData}
                        inputs={this.generateInputs()}
                        backRoute={paths.controlTowers}
                        onSubmit={this.handleSubmit}
                        onChange={this.handleFormChange}
                        loader={isSavingTower}
                    />
                )}
            </App>
        );
    }
}

const ControlTowersAddWithTranslation = withGlobalStyles(withTranslation(['towers', 'common'])(ControlTowersAdd));

const mapStateToProps = (state) => ({
    towers: towersSelectors.towers(state),
    cudTowersError: errorSelectors.cudControlTowerError(state),
    isSavingTower: pendingSelectors.isPending(state, towersTypes.CREATE_TOWER, towersTypes.EDIT_TOWER),
});

const mapDispatchToProps = {
    getTowers: towersActions.getTowers,
    createTower: towersActions.createTower,
    editTower: towersActions.editTower,
    deleteTower: towersActions.deleteTower,
};

export default connect(mapStateToProps, mapDispatchToProps)(ControlTowersAddWithTranslation);
